import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, Grid, Paper, TextField, InputAdornment, CircularProgress, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchUtils from "../utils/SearchUtils";

const SCAN_DEBOUNCE_MS = 300;
const BIN_DEBOUNCE_MS = 800; // Delay before auto-validating and jumping to Save

const ScanSection = ({
  t,
  prescriptionAssign,
  rxNumber,
  onScanChange,
  scanRxInputRef,
  binId,
  onBinIdChange,
  onLocationBlur,
  onLocationKeyPress,
  locationError,
  isValidatingLocation,
  validatedLocation,
  enableTrim,
  trimChars,
  isEpicHasValidRx,
  isEpicHasValidRxMessage,
}) => {
  const [localRxNumber, setLocalRxNumber] = useState(rxNumber || "");
  const [localBinId, setLocalBinId] = useState(binId || "");
  const debounceTimerRef = useRef(null);
  const binDebounceTimerRef = useRef(null);
  const latestValueRef = useRef(rxNumber || "");

  // Sync local value when parent clears or sets rxNumber (e.g. after scan success)
  useEffect(() => {
    setLocalRxNumber(rxNumber || "");
    latestValueRef.current = rxNumber || "";
  }, [rxNumber]);

  // Sync local value when parent sets/clears binId
  useEffect(() => {
    setLocalBinId(binId || "");
  }, [binId]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (binDebounceTimerRef.current) {
        clearTimeout(binDebounceTimerRef.current);
      }
    };
  }, []);

  const handleScanChange = (value) => {
    setLocalRxNumber(value);
    latestValueRef.current = value;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      onScanChange(value);
    }, SCAN_DEBOUNCE_MS);
  };

  const handleBinIdChange = (rawValue) => {
    const trimmedValue = enableTrim
      ? SearchUtils.trimLeadingTrailing(rawValue, trimChars)
      : rawValue.trim();

    setLocalBinId(rawValue); // Update visual field immediately

    if (onBinIdChange) {
      onBinIdChange(trimmedValue); // Update parent state immediately
    }

    // Clear existing timer
    if (binDebounceTimerRef.current) {
      clearTimeout(binDebounceTimerRef.current);
      binDebounceTimerRef.current = null;
    }

    // Only start timer if we have content
    if (trimmedValue) {
      binDebounceTimerRef.current = setTimeout(() => {
        binDebounceTimerRef.current = null;
        // Trigger validation and focus jump (re-using onLocationBlur logic)
        if (onLocationBlur) {
          onLocationBlur();
        }
      }, BIN_DEBOUNCE_MS);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Clear any pending debounce and fire immediately
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      // Use ref to avoid stale closure - always has the latest typed value
      const currentValue = latestValueRef.current;
      if (currentValue && currentValue.trim().length > 0) {
        onScanChange(currentValue);
      }
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        mb: 3,
        background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
        border: "1px solid #e2e8f0",
        borderRadius: 3,
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
      }}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6}>
          <TextField
            inputRef={scanRxInputRef}
            variant="outlined"
            placeholder={t("Assign.ScanRxNow") || "Scan RX Now"}
            value={localRxNumber}
            onChange={(e) => handleScanChange(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={!prescriptionAssign}
            fullWidth
            InputProps={{
              startAdornment: (
                <Box
                  sx={{
                    mr: 1.5,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <SearchIcon
                    sx={{
                      color: prescriptionAssign ? "#7c3aed" : "#b0bec5",
                      fontSize: 24,
                    }}
                  />
                </Box>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "#ffffff",
                border: "2px solid #e2e8f0",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: "#7c3aed",
                },
                "&.Mui-focused": {
                  borderColor: "#7c3aed",
                  boxShadow: "0 0 0 3px rgba(124, 58, 237, 0.1)",
                },
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            variant="outlined"
            placeholder={t("Assign.Location") || "Location"}
            value={localBinId}
            onChange={(e) => handleBinIdChange(e.target.value)}
            onBlur={onLocationBlur}
            onKeyPress={(e) => {
              // If user hits enter, clear the timer and trigger immediately
              if (e.key === 'Enter') {
                if (binDebounceTimerRef.current) {
                  clearTimeout(binDebounceTimerRef.current);
                  binDebounceTimerRef.current = null;
                }
                if (onLocationKeyPress) onLocationKeyPress(e);
              } else if (onLocationKeyPress) {
                onLocationKeyPress(e);
              }
            }}
            disabled={!prescriptionAssign}
            required
            error={Boolean(locationError)}
            helperText={locationError || ""}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {isValidatingLocation ? (
                    <CircularProgress size={16} sx={{ color: "#7c3aed" }} />
                  ) : validatedLocation ? (
                    <CheckCircleIcon sx={{ color: "#10b981", fontSize: 20 }} />
                  ) : null}
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "#ffffff",
                border: locationError
                  ? "2px solid #ef4444"
                  : validatedLocation
                    ? "2px solid #10b981"
                    : "2px solid #e2e8f0",
                transition: "all 0.3s ease",
                fontSize: "0.875rem",
                "&:hover": {
                  borderColor: locationError
                    ? "#ef4444"
                    : validatedLocation
                      ? "#10b981"
                      : "#7c3aed",
                },
                "&.Mui-focused": {
                  borderColor: locationError
                    ? "#ef4444"
                    : validatedLocation
                      ? "#10b981"
                      : "#7c3aed",
                  boxShadow: locationError
                    ? "0 0 0 3px rgba(239, 68, 68, 0.1)"
                    : validatedLocation
                      ? "0 0 0 3px rgba(16, 185, 129, 0.1)"
                      : "0 0 0 3px rgba(124, 58, 237, 0.1)",
                },
              },
              "& .MuiInputBase-input::placeholder": {
                fontSize: "0.875rem",
                color: "#9e9e9e",
                opacity: 1,
              },
              "& .MuiFormHelperText-root": {
                fontSize: "0.75rem",
                marginTop: "4px",
              },
            }}
          />
        </Grid>
        {isEpicHasValidRx === false && isEpicHasValidRxMessage && (
          <Box sx={{ mt: 2, width: "100%", ml: 2 }}>
            <Typography
              variant="body2"
              sx={{ color: "#ef4444", fontSize: "0.8rem" }}
            >
              {isEpicHasValidRxMessage}
            </Typography>
          </Box>
        )}
      </Grid>
    </Paper>
  );
};

ScanSection.propTypes = {
  t: PropTypes.func.isRequired,
  prescriptionAssign: PropTypes.bool,
  rxNumber: PropTypes.string,
  onScanChange: PropTypes.func.isRequired,
  scanRxInputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  binId: PropTypes.string,
  onBinIdChange: PropTypes.func,
  onLocationBlur: PropTypes.func,
  onLocationKeyPress: PropTypes.func,
  locationError: PropTypes.string,
  isValidatingLocation: PropTypes.bool,
  validatedLocation: PropTypes.object,
  enableTrim: PropTypes.bool,
  trimChars: PropTypes.string,
  isEpicHasValidRx: PropTypes.bool,
  isEpicHasValidRxMessage: PropTypes.string,
};

ScanSection.defaultProps = {
  prescriptionAssign: true,
  rxNumber: "",
  scanRxInputRef: null,
  binId: "",
  onBinIdChange: undefined,
  onLocationBlur: undefined,
  onLocationKeyPress: undefined,
  locationError: "",
  isValidatingLocation: false,
  validatedLocation: null,
  enableTrim: false,
  trimChars: SearchUtils.defaultTrimChars,
};

export default ScanSection;



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/components/ScanSection.jsx